import React, { useState, useMemo, useEffect } from "react";
import { useCart } from "./CartContext";
import "./CartPopup.css";

const MIN_ORDER = 1999;

const CartPopup = () => {
  const { cartItems, clearCart, addToCart, removeFromCart } = useCart();

  const [showModal, setShowModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    pincode: "",
    agentId: "",
  });

  const totalItems = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cartItems]
  );

  const totalPrice = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) =>
          sum + (parseFloat(item.discountPrice || item.price || 0) || 0) * (item.quantity || 0),
        0
      ),
    [cartItems]
  );

  const isMinMet = totalPrice >= MIN_ORDER;

  const closeModal = React.useCallback(() => {
    setShowModal(false);
    setSendSuccess(false);
    setErrors({});
    setFormData((prev) => (sendSuccess ? {
      name: "",
      phone: "",
      email: "",
      address: "",
      pincode: "",
      agentId: "",
    } : prev));
  }, [sendSuccess]);

  // Lock background scroll when modal is open & handle Escape key
  useEffect(() => {
    if (showModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          closeModal();
        }
      };
      window.addEventListener("keydown", handleKeyDown);

      return () => {
        document.body.style.overflow = originalOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [showModal, closeModal]);

  if (cartItems.length === 0 && !sendSuccess) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) {
      setErrors((p) => ({ ...p, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Full Name is required";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone Number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/[\s-+()]/g, ""))) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = "Enter a valid 6-digit pincode";
    }
    if (!formData.address.trim()) {
      newErrors.address = "Delivery Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = async () => {
    if (totalPrice < MIN_ORDER) {
      setErrors({ form: `Minimum order value of ₹${MIN_ORDER} required to submit enquiry.` });
      return;
    }

    if (!validateForm()) return;

    setIsSending(true);
    setErrors({});

    const API_URL = process.env.REACT_APP_API_URL || "https://api.advaytraders.in";

    try {
      const payload = {
        customer: {
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim().toLowerCase(),
          address: formData.address.trim(),
          pincode: formData.pincode.trim(),
          agentId: formData.agentId.trim(),
        },
        cart: cartItems,
        totalItems,
        totalPrice,
      };

      const res = await fetch(`${API_URL}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Server error while saving enquiry");

      setSendSuccess(true);
      clearCart();
    } catch (err) {
      console.error("Failed to save enquiry:", err);
      setErrors({ form: err.message || "Failed to send enquiry. Please check your connection and try again." });
    } finally {
      setIsSending(false);
    }
  };


  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("modal-overlay")) {
      closeModal();
    }
  };

  return (
    <>
      {/* Sticky Bottom Cart Bar */}
      <div className={`cart-bar ${!isMinMet ? "min-warning" : ""}`}>
        <div className="cart-info">
          <div className="cart-badge-icon">
            <i className="fas fa-shopping-cart"></i>
          </div>
          <div className="cart-text-group">
            <span className="cart-items-count">
              <strong>{totalItems}</strong> {totalItems === 1 ? "item" : "items"}
            </span>
            <span className="cart-divider">•</span>
            <span className="cart-total-price">
              ₹{totalPrice.toFixed(2)}
            </span>
          </div>

          {!isMinMet && (
            <span className="min-order-pill">
              Min. Order ₹{MIN_ORDER}
            </span>
          )}
        </div>

        <button
          className="cart-action-btn"
          disabled={!isMinMet}
          onClick={() => setShowModal(true)}
          title={!isMinMet ? `Add ₹${(MIN_ORDER - totalPrice).toFixed(2)} more to checkout` : "Checkout and send enquiry"}
        >
          <span>View Cart & Enquiry</span>
          <i className="fas fa-arrow-right ms-1"></i>
        </button>
      </div>

      {/* Cart Summary & Enquiry Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true">
          <div className="modal-card wide">
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-header-title">
                <i className={sendSuccess ? "fas fa-check-circle me-2" : "fas fa-shopping-basket me-2"}></i>
                <h3>{sendSuccess ? "Enquiry Placed Successfully" : "Cart Summary & Enquiry"}</h3>
              </div>
              <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body two-column">
              {sendSuccess ? (
                <div className="success-ui full">
                  <div className="check-icon-circle">
                    <i className="fas fa-check"></i>
                  </div>
                  <h2>Thank You!</h2>
                  <p className="success-subtitle">Your festive cracker enquiry has been received.</p>
                  <p className="success-desc">
                    Our sales team will contact you shortly on <strong>{formData.phone || "your number"}</strong> with order confirmation and payment details.
                  </p>
                  <button className="success-done-btn" onClick={closeModal}>
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <>
                  {/* LEFT COLUMN: CART SUMMARY */}
                  <div className="cart-column">
                    <div className="column-header">
                      <h4>Order Items ({totalItems})</h4>
                      <span className="cart-summary-total-tag">₹{totalPrice.toFixed(2)}</span>
                    </div>

                    {cartItems.length === 0 ? (
                      <div className="empty-cart-summary">
                        <p>Your cart is empty.</p>
                      </div>
                    ) : (
                      <div className="cart-table-wrapper">
                        <table className="cart-summary-table">
                          <thead>
                            <tr>
                              <th className="th-cart-product">Product Name</th>
                              <th className="th-cart-qty">Qty</th>
                              <th className="th-cart-price">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cartItems.map((item, i) => {
                              const itemPrice = parseFloat(item.discountPrice || item.price || 0);
                              const itemTotal = itemPrice * item.quantity;

                              return (
                                <tr key={item._id || i}>
                                  <td className="td-cart-product">
                                    <span className="cart-item-name">{item.name}</span>
                                    {item.Unit && (
                                      <span className="cart-item-unit">({item.Unit})</span>
                                    )}
                                  </td>
                                  <td className="td-cart-qty">
                                    <div className="cart-mini-stepper">
                                      <button
                                        type="button"
                                        onClick={() => removeFromCart(item._id)}
                                        title="Decrease quantity"
                                      >
                                        -
                                      </button>
                                      <span>{item.quantity}</span>
                                      <button
                                        type="button"
                                        onClick={() => addToCart(item, false)}
                                        title="Increase quantity"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </td>
                                  <td className="td-cart-price">
                                    ₹{itemTotal.toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="cart-table-total-row">
                              <td colSpan="2" className="td-cart-total-label">
                                Total Price:
                              </td>
                              <td className="td-cart-total-value">
                                ₹{totalPrice.toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                    {!isMinMet && (
                      <div className="min-order-alert">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Minimum order of ₹{MIN_ORDER} required (Add ₹{(MIN_ORDER - totalPrice).toFixed(2)} more).
                      </div>
                    )}
                  </div>

                  {/* RIGHT COLUMN: ENQUIRY FORM */}
                  <div className="form-column">
                    <div className="column-header">
                      <h4>Delivery Details</h4>
                      <span className="required-hint">* All fields mandatory</span>
                    </div>

                    {errors.form && (
                      <div className="form-alert-error">
                        <i className="fas fa-info-circle me-1"></i>
                        {errors.form}
                      </div>
                    )}

                    <div className="form-fields-container">
                      <div className="form-field-group">
                        <label className="form-label" htmlFor="popup-name">Full Name *</label>
                        <input
                          id="popup-name"
                          name="name"
                          type="text"
                          className={`form-input ${errors.name ? "has-error" : ""}`}
                          placeholder="e.g. Ramesh Kumar"
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && <span className="field-error-text">{errors.name}</span>}
                      </div>

                      <div className="form-field-row">
                        <div className="form-field-group flex-1">
                          <label className="form-label" htmlFor="popup-phone">Mobile Phone *</label>
                          <input
                            id="popup-phone"
                            name="phone"
                            type="tel"
                            className={`form-input ${errors.phone ? "has-error" : ""}`}
                            placeholder="10-digit number"
                            value={formData.phone}
                            onChange={handleChange}
                            maxLength={13}
                          />
                          {errors.phone && <span className="field-error-text">{errors.phone}</span>}
                        </div>

                        <div className="form-field-group flex-1">
                          <label className="form-label" htmlFor="popup-pincode">Pincode *</label>
                          <input
                            id="popup-pincode"
                            name="pincode"
                            type="text"
                            className={`form-input ${errors.pincode ? "has-error" : ""}`}
                            placeholder="6-digit pincode"
                            value={formData.pincode}
                            onChange={handleChange}
                            maxLength={6}
                          />
                          {errors.pincode && <span className="field-error-text">{errors.pincode}</span>}
                        </div>
                      </div>

                      <div className="form-field-group">
                        <label className="form-label" htmlFor="popup-email">Email Address *</label>
                        <input
                          id="popup-email"
                          name="email"
                          type="email"
                          className={`form-input ${errors.email ? "has-error" : ""}`}
                          placeholder="name@example.com"
                          value={formData.email}
                          onChange={handleChange}
                        />
                        {errors.email && <span className="field-error-text">{errors.email}</span>}
                      </div>

                      <div className="form-field-group">
                        <label className="form-label" htmlFor="popup-address">Delivery Address *</label>
                        <textarea
                          id="popup-address"
                          name="address"
                          rows="3"
                          className={`form-textarea ${errors.address ? "has-error" : ""}`}
                          placeholder="Door No, Street Name, Area, Landmark, City"
                          value={formData.address}
                          onChange={handleChange}
                        />
                        {errors.address && <span className="field-error-text">{errors.address}</span>}
                      </div>

                      <div className="form-field-group">
                        <label className="form-label" htmlFor="popup-agent">Agent ID / Referral (Optional)</label>
                        <input
                          id="popup-agent"
                          name="agentId"
                          type="text"
                          className="form-input"
                          placeholder="Leave blank if none"
                          value={formData.agentId}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            {!sendSuccess && (
              <div className="modal-footer">
                <button
                  type="button"
                  className="modal-submit-btn"
                  onClick={handleSend}
                  disabled={isSending || !isMinMet || cartItems.length === 0}
                >
                  {isSending ? (
                    <>
                      <span className="spinner-border-sm me-2"></span>
                      Submitting Enquiry...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Submit Enquiry (₹{totalPrice.toFixed(2)})
                    </>
                  )}
                </button>
                <button type="button" className="modal-cancel-btn" onClick={closeModal} disabled={isSending}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CartPopup;
